import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@/components/ui";
import Modal from "../../ui/UIElements/Modal";
import { PhoneInputSimple } from "../../ui/UIElements/PhoneInput";
import { useToast } from "@finitless/shared/hooks";
import { handleApiError, handleApiSuccess } from "@finitless/shared/lib";
import {
  useAgentTemplates,
  useRefreshAgentTemplates,
  useSyncAgentTemplates,
  useSendAgentTemplate,
} from "@/hooks/useAiAgents";

const STATUS_BADGE_CLASSES = {
  APPROVED: "bg-green-900/40 text-green-300 border-green-700",
  PENDING: "bg-yellow-900/40 text-yellow-300 border-yellow-700",
  IN_APPEAL: "bg-yellow-900/40 text-yellow-300 border-yellow-700",
  REINSTATED: "bg-yellow-900/40 text-yellow-300 border-yellow-700",
  REJECTED: "bg-red-900/40 text-red-300 border-red-700",
  DISABLED: "bg-red-900/40 text-red-300 border-red-700",
  FLAGGED: "bg-red-900/40 text-red-300 border-red-700",
  PAUSED: "bg-red-900/40 text-red-300 border-red-700",
  LIMIT_EXCEEDED: "bg-red-900/40 text-red-300 border-red-700",
  LOCKED: "bg-red-900/40 text-red-300 border-red-700",
  ERROR: "bg-red-900/40 text-red-300 border-red-700",
  NOT_SYNCED: "bg-gray-700/60 text-gray-300 border-gray-600",
  ARCHIVED: "bg-gray-700/60 text-gray-300 border-gray-600",
  DELETED: "bg-gray-700/60 text-gray-300 border-gray-600",
  PENDING_DELETION: "bg-gray-700/60 text-gray-300 border-gray-600",
  DELETING: "bg-gray-700/60 text-gray-300 border-gray-600",
};

const QUALITY_BADGE_CLASSES = {
  GREEN: "bg-green-900/40 text-green-300 border-green-700",
  YELLOW: "bg-yellow-900/40 text-yellow-300 border-yellow-700",
  RED: "bg-red-900/40 text-red-300 border-red-700",
  UNKNOWN: "bg-gray-700/60 text-gray-300 border-gray-600",
};

const REJECTION_REASON_VISIBLE_ON = new Set([
  "REJECTED",
  "FLAGGED",
  "PAUSED",
  "DISABLED",
  "LIMIT_EXCEEDED",
  "ERROR",
]);

// Supported dashboard locales — these are the only languages operators can
// author templates in today (see apps/dashboard/src/i18n/index.js). Labels
// come from the i18n namespace (templates.filters.languages.*).
const SUPPORTED_LANGUAGES = ["en", "es", "fr", "ar"];

/**
 * Extract the number of {{N}} placeholders from a BODY component text.
 * Returns the count so we can render one input per placeholder.
 */
const countBodyPlaceholders = (bodyText) => {
  if (!bodyText) return 0;
  const matches = bodyText.match(/\{\{\d+\}\}/g);
  return matches ? matches.length : 0;
};

/** A template can be sent when it is APPROVED + MARKETING. */
const isSendable = (tpl) =>
  tpl?.sync_status === "APPROVED" && tpl?.category === "MARKETING";

const statusBadgeClass = (status) =>
  STATUS_BADGE_CLASSES[status] ||
  "bg-gray-700/60 text-gray-300 border-gray-600";

const qualityBadgeClass = (score) =>
  QUALITY_BADGE_CLASSES[score] || QUALITY_BADGE_CLASSES.UNKNOWN;

/** Phone number (with country code, no '+') must be 10–15 digits per the API spec. */
const isPhoneValid = (phone) => /^\d{10,15}$/.test(phone);

/**
 * Modal form to send a marketing template message to a phone number.
 */
const SendTemplateModal = ({
  isOpen,
  templateId,
  templateName,
  bodyText,
  placeholderCount,
  isPending,
  onSend,
  onClose,
  t,
}) => {
  const [phone, setPhone] = useState("");
  const [params, setParams] = useState(() =>
    new Array(placeholderCount).fill(""),
  );

  const updateParam = (index, value) =>
    setParams((prev) => prev.map((v, i) => (i === index ? value : v)));

  // Live preview: replace {{N}} placeholders with the current param values.
  const previewText = useMemo(() => {
    if (!bodyText) return null;
    return params.reduce(
      (text, val, idx) =>
        text.replace(`{{${idx + 1}}}`, val || `{{${idx + 1}}}`),
      bodyText,
    );
  }, [bodyText, params]);

  // Strip the leading "+" so the value matches the API format (e.g. 573008159947).
  const normalizedPhone = phone?.replace(/^\+/, "").trim() || "";
  const allParamsFilled =
    placeholderCount === 0 || params.every((p) => p.trim() !== "");
  const isValid = isPhoneValid(normalizedPhone) && allParamsFilled;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isValid) return;
    onSend(
      templateId,
      normalizedPhone,
      params.filter((p) => p !== ""),
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("templates.send.modalTitle")}
      subtitle={templateName}
      maxWidth="max-w-md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <PhoneInputSimple
            value={phone}
            onChange={(val) => setPhone(val || "")}
            label={t("templates.send.phoneLabel")}
            placeholder={t("templates.send.phonePlaceholder")}
            size="md"
          />
        </div>
        {placeholderCount > 0 && (
          <div className="space-y-3">
            <p className="text-sm font-medium text-gray-300">
              {t("templates.send.parametersLabel")}
            </p>
            {params.map((val, idx) => (
              <input
                key={idx}
                type="text"
                value={val}
                onChange={(e) => updateParam(idx, e.target.value)}
                placeholder={`${t("templates.send.parameterPlaceholder")} {{${idx + 1}}}`}
                className="w-full rounded-lg border border-gray-600 bg-gray-800 px-3 py-2 text-sm text-gray-200 placeholder-gray-500 focus:border-purple-500 focus:outline-none"
              />
            ))}
          </div>
        )}
        {previewText && (
          <div>
            <p className="mb-1 text-sm font-medium text-gray-300">
              {t("templates.send.previewLabel")}
            </p>
            <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-3 text-sm whitespace-pre-wrap text-gray-200">
              {previewText}
            </div>
          </div>
        )}
        <div className="border-t border-gray-700 pt-4">
          <button
            type="submit"
            disabled={!isValid || isPending}
            className="inline-flex w-full items-center justify-center rounded-lg bg-green-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-gray-600"
          >
            <Icon name="Send" className="mr-2 h-4 w-4" />
            {isPending
              ? t("templates.send.sending")
              : t("templates.send.submit")}
          </button>
        </div>
      </form>
    </Modal>
  );
};

/**
 * AgentTemplatesSection — read-only list of WhatsApp message templates attached
 * to the agent's WABA, with a "refresh from Meta" button.
 * Only mount this for agents where provider === "whatsapp".
 */
const AgentTemplatesSection = ({ agent, showHeader = true }) => {
  const { t } = useTranslation("aiAgents");
  const toast = useToast();

  const {
    data: templates = [],
    isLoading,
    isError,
    refetch,
  } = useAgentTemplates(agent?.id);

  const refreshMutation = useRefreshAgentTemplates();
  const syncMutation = useSyncAgentTemplates();
  const sendMutation = useSendAgentTemplate();

  // Track which template id has the send form open (null = none).
  const [sendFormOpenFor, setSendFormOpenFor] = useState(null);

  const [nameFilter, setNameFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [languageFilter, setLanguageFilter] = useState("");

  // Unique statuses actually present in the current dataset — so the dropdown
  // only offers options the user can meaningfully pick.
  const availableStatuses = useMemo(() => {
    const set = new Set();
    for (const tpl of templates) {
      if (tpl?.sync_status) set.add(tpl.sync_status);
    }
    return Array.from(set).sort();
  }, [templates]);

  const filteredTemplates = useMemo(() => {
    const needle = nameFilter.trim().toLowerCase();
    return templates.filter((tpl) => {
      if (needle && !tpl?.name?.toLowerCase().includes(needle)) return false;
      if (statusFilter && tpl?.sync_status !== statusFilter) return false;
      if (languageFilter && tpl?.language !== languageFilter) return false;
      return true;
    });
  }, [templates, nameFilter, statusFilter, languageFilter]);

  const handleRefresh = async () => {
    try {
      await refreshMutation.mutateAsync({ agentId: agent.id });
      handleApiSuccess(t("templates.refreshSuccess"), toast);
    } catch (error) {
      handleApiError(error, toast, {
        title: t("templates.refreshError"),
        defaultMessage: t("templates.refreshError"),
      });
    }
  };

  const handleSync = async () => {
    try {
      await syncMutation.mutateAsync({ agentId: agent.id });
      handleApiSuccess(t("templates.syncSuccess"), toast);
    } catch (error) {
      handleApiError(error, toast, {
        title: t("templates.syncError"),
        defaultMessage: t("templates.syncError"),
      });
    }
  };

  const handleSend = async (templateId, to, bodyParameters) => {
    try {
      await sendMutation.mutateAsync({
        agentId: agent.id,
        templateId,
        to,
        bodyParameters,
      });
      handleApiSuccess(t("templates.send.success"), toast);
      setSendFormOpenFor(null);
    } catch (error) {
      handleApiError(error, toast, {
        title: t("templates.send.error"),
        defaultMessage: t("templates.send.error"),
      });
    }
  };

  const formatStatusLabel = (status) =>
    t(`templates.status.${status}`, { defaultValue: status });
  const formatQualityLabel = (score) =>
    t(`templates.qualityScore.${score}`, { defaultValue: score });
  const formatRejectionLabel = (reason) =>
    t(`templates.rejectionReason.${reason}`, { defaultValue: reason });

  return (
    <div>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        {showHeader ? (
          <div>
            <h3 className="text-lg font-semibold text-white">
              {t("templates.title")}
            </h3>
            <p className="mt-1 text-sm text-gray-400">
              {t("templates.description")}
            </p>
          </div>
        ) : (
          <div />
        )}
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={handleRefresh}
            disabled={
              refreshMutation.isPending || syncMutation.isPending || isLoading
            }
            className="inline-flex items-center rounded-lg border border-gray-600 bg-gray-800 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-700 disabled:cursor-not-allowed disabled:bg-gray-800/50 disabled:text-gray-500"
          >
            <Icon
              name="RefreshCw"
              className={`mr-2 h-4 w-4 ${
                refreshMutation.isPending ? "animate-spin" : ""
              }`}
            />
            {refreshMutation.isPending
              ? t("templates.refreshing")
              : t("templates.refreshButton")}
          </button>
          <button
            type="button"
            onClick={handleSync}
            disabled={
              syncMutation.isPending || refreshMutation.isPending || isLoading
            }
            className="inline-flex items-center rounded-lg bg-purple-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-purple-700 disabled:cursor-not-allowed disabled:bg-gray-600"
          >
            <Icon
              name="UploadCloud"
              className={`mr-2 h-4 w-4 ${
                syncMutation.isPending ? "animate-spin" : ""
              }`}
            />
            {syncMutation.isPending
              ? t("templates.syncing")
              : t("templates.syncButton")}
          </button>
        </div>
      </div>

      {/* Local filters — operate on the already-fetched list, no API calls. */}
      {!isLoading && !isError && templates.length > 0 && (
        <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Icon
              name="Search"
              className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-500"
            />
            <input
              type="search"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              placeholder={t("templates.filters.namePlaceholder")}
              aria-label={t("templates.filters.nameLabel")}
              className="w-full rounded-lg border border-gray-600 bg-gray-800 py-2 pr-3 pl-9 text-sm text-gray-200 placeholder-gray-500 focus:border-purple-500 focus:outline-none"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            aria-label={t("templates.filters.statusLabel")}
            className="appearance-none rounded-lg border border-gray-600 bg-gray-800 bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20viewBox%3D%220%200%2020%2020%22%20fill%3D%22%239ca3af%22%3E%3Cpath%20fill-rule%3D%22evenodd%22%20d%3D%22M5.23%207.21a.75.75%200%200%201%201.06.02L10%2011.06l3.71-3.83a.75.75%200%200%201%201.08%201.04l-4.25%204.39a.75.75%200%200%201-1.08%200L5.21%208.27a.75.75%200%200%201%20.02-1.06z%22%20clip-rule%3D%22evenodd%22/%3E%3C/svg%3E')] bg-[length:16px] bg-[position:right_0.625rem_center] bg-no-repeat py-2 pr-9 pl-3 text-sm text-gray-200 focus:border-purple-500 focus:outline-none"
          >
            <option value="">{t("templates.filters.statusAll")}</option>
            {availableStatuses.map((status) => (
              <option key={status} value={status}>
                {formatStatusLabel(status)}
              </option>
            ))}
          </select>
          <select
            value={languageFilter}
            onChange={(e) => setLanguageFilter(e.target.value)}
            aria-label={t("templates.filters.languageLabel")}
            className="appearance-none rounded-lg border border-gray-600 bg-gray-800 bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20viewBox%3D%220%200%2020%2020%22%20fill%3D%22%239ca3af%22%3E%3Cpath%20fill-rule%3D%22evenodd%22%20d%3D%22M5.23%207.21a.75.75%200%200%201%201.06.02L10%2011.06l3.71-3.83a.75.75%200%200%201%201.08%201.04l-4.25%204.39a.75.75%200%200%201-1.08%200L5.21%208.27a.75.75%200%200%201%20.02-1.06z%22%20clip-rule%3D%22evenodd%22/%3E%3C/svg%3E')] bg-[length:16px] bg-[position:right_0.625rem_center] bg-no-repeat py-2 pr-9 pl-3 text-sm text-gray-200 focus:border-purple-500 focus:outline-none"
          >
            <option value="">{t("templates.filters.languageAll")}</option>
            {SUPPORTED_LANGUAGES.map((lang) => (
              <option key={lang} value={lang}>
                {t(`templates.filters.languages.${lang}`)}
              </option>
            ))}
          </select>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-16 animate-pulse rounded-lg border border-gray-700 bg-gray-800"
            />
          ))}
        </div>
      ) : isError ? (
        <div className="rounded-lg border border-red-700 bg-red-900/30 p-4">
          <p className="text-sm text-red-300">{t("templates.loadError")}</p>
          <button
            type="button"
            onClick={() => refetch()}
            className="mt-2 inline-flex items-center rounded bg-red-800/60 px-3 py-1.5 text-xs font-medium text-red-100 hover:bg-red-700"
          >
            <Icon name="RefreshCw" className="mr-1.5 h-3 w-3" />
            {t("templates.retry")}
          </button>
        </div>
      ) : templates.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-700 bg-gray-800/40 p-6 text-center">
          <p className="text-sm text-gray-400">{t("templates.empty")}</p>
        </div>
      ) : filteredTemplates.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-700 bg-gray-800/40 p-6 text-center">
          <p className="text-sm text-gray-400">
            {t("templates.filters.emptyFiltered")}
          </p>
        </div>
      ) : (
        <ul className="space-y-2">
          {filteredTemplates.map((tpl) => {
            const status = tpl.sync_status;
            const showReason =
              tpl.reason_status &&
              tpl.reason_status !== "NONE" &&
              REJECTION_REASON_VISIBLE_ON.has(status);
            const bodyComponent = Array.isArray(tpl.components)
              ? tpl.components.find((c) => c?.type === "BODY")
              : null;
            const bodyText = bodyComponent?.text;
            const footerComponent = Array.isArray(tpl.components)
              ? tpl.components.find((c) => c?.type === "FOOTER")
              : null;
            const footerText =
              typeof footerComponent?.text === "string"
                ? footerComponent.text
                : null;

            return (
              <li
                key={`${tpl.id}-${tpl.language}`}
                className="rounded-lg border border-gray-700 bg-gray-800/60 p-4"
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p
                        className="truncate font-mono text-sm font-medium text-white"
                        title={tpl.name}
                      >
                        {tpl.name}
                      </p>
                      {tpl.category && (
                        <span className="rounded border border-gray-600 bg-gray-900/60 px-2 py-0.5 text-xs text-gray-300">
                          {tpl.category}
                        </span>
                      )}
                      <span className="rounded border border-gray-600 bg-gray-900/60 px-2 py-0.5 text-xs text-gray-300 uppercase">
                        {tpl.language}
                      </span>
                    </div>
                    {bodyText && (
                      <p className="mt-2 rounded border border-gray-700 bg-gray-900/40 p-2 text-xs whitespace-pre-wrap text-gray-200">
                        {bodyText}
                      </p>
                    )}
                    {footerText && (
                      <p className="mt-1 text-xs whitespace-pre-wrap text-gray-500 italic">
                        {footerText}
                      </p>
                    )}
                    {showReason && (
                      <p className="mt-1 text-xs text-red-300">
                        {t("templates.columns.reason")}:{" "}
                        {formatRejectionLabel(tpl.reason_status)}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                    <span
                      className={`rounded border px-2 py-0.5 text-xs font-medium ${statusBadgeClass(
                        status,
                      )}`}
                    >
                      {formatStatusLabel(status)}
                    </span>
                    {tpl.quality_score && (
                      <span
                        className={`rounded border px-2 py-0.5 text-xs font-medium ${qualityBadgeClass(
                          tpl.quality_score,
                        )}`}
                      >
                        {t("templates.columns.qualityScore")}:{" "}
                        {formatQualityLabel(tpl.quality_score)}
                      </span>
                    )}
                    {isSendable(tpl) && (
                      <button
                        type="button"
                        onClick={() => setSendFormOpenFor(tpl.id)}
                        className="inline-flex items-center rounded border border-green-700 bg-green-900/40 px-2 py-0.5 text-xs font-medium text-green-300 transition-colors hover:bg-green-800/60"
                      >
                        <Icon name="Send" className="mr-1 h-3 w-3" />
                        {t("templates.send.button")}
                      </button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Send template modal — rendered once, outside the list */}
      {sendFormOpenFor != null &&
        (() => {
          const tpl = templates.find((t) => t.id === sendFormOpenFor);
          if (!tpl || !isSendable(tpl)) return null;
          const body = Array.isArray(tpl.components)
            ? tpl.components.find((c) => c?.type === "BODY")
            : null;
          return (
            <SendTemplateModal
              isOpen
              templateId={tpl.id}
              templateName={tpl.name}
              bodyText={body?.text || ""}
              placeholderCount={countBodyPlaceholders(body?.text)}
              isPending={sendMutation.isPending}
              onSend={handleSend}
              onClose={() => setSendFormOpenFor(null)}
              t={t}
            />
          );
        })()}
    </div>
  );
};

export default AgentTemplatesSection;
